/*
 * Copyright (c) 2022 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */
import * as moment from 'moment';

import { Injectable } from '@angular/core';
import { appConstants } from '../data-model/app-constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { Logger } from '@core/lib/services';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { BaseService } from './base.service';
import { PatientInfo, PatientData, PatientUpdateDetails } from '../data-model/patient.types';
import { AuditRecord_Patient, ProxyRequest } from '../data-model/proxy.types';
import { DataTransformationService } from '@app/common/data-transformation.service';

const log = new Logger('PatientContextService');

@Injectable({
  providedIn: 'root'
})
export class PatientContextService {
  // Local Property
  private patientData: PatientData = {};

  // Observer
  private patientDataObserver: BehaviorSubject<PatientData> = new BehaviorSubject({});
  getPatientData(): Observable<PatientData> {
    return this.patientDataObserver.asObservable();
  }

  setPatientData(patientData: PatientData) {
    this.patientDataObserver.next(patientData);
  }
  // Constructor
  constructor(
    protected dataService: ProxyDataService,
    private baseService: BaseService,
    private dataTransformationService: DataTransformationService
  ) {}

  // Service initialize
  initialize(patientId: number): void {
    Promise.all([this.getPatientRecord(patientId), this.getPatientOrders(patientId)]).finally(() => {
      this.setPatientData(this.patientData);
    });
  }

  // Function to load the patient data
  getPatientRecord(patientId: number): Promise<PatientInfo> {
    return new Promise<PatientInfo>((resolve, reject) => {
      log.debug('RM Patient Service - fetchPatientContext invoked', patientId);

      this.baseService.getServiceDetails().subscribe(details => {
        const request: ProxyRequest = { ...details?.patient?.get };
        if (request && request.configValue && patientId) {
          request.configValue = request.configValue.replace('{id}', patientId.toString());

          this.baseService
            .loadData(appConstants.appURLs.proxyFetch, request)
            .then(data => {
              data.age = data.dob ? this.dataTransformationService.calculateStudyAge(data.dob, moment()) : '';
              this.patientData.patientInfo = data as PatientInfo;
              log.debug('RM Patient Service - fetchPatientContext response', data);
              resolve(data as PatientInfo);
            })
            .catch(ex => {
              log.error('RecordMgmt fetchPatientContext : Exception', ex);
              reject(ex);
            });
        }
      });
    });
  }

  // Function to update the patient data
  updatePatientRecord(patientDetails: PatientUpdateDetails, auditRecord: AuditRecord_Patient): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      log.debug('RM Patient Service - updatePatientRecord invoked', patientDetails);

      this.baseService.getServiceDetails().subscribe(details => {
        const request: ProxyRequest = { ...details?.patient?.edit };
        if (request && request.configValue && Object.keys(patientDetails).length) {
          request.payload = patientDetails;
          request.auditRecord = auditRecord;

          this.baseService
            .loadData(appConstants.appURLs.proxyFetch, request)
            .then(() => {
              log.debug('RM Patient Service - updatePatientRecord success response');
              resolve();
            })
            .catch(ex => {
              log.error('RecordMgmt updatePatientRecord : Exception', ex);
              reject(ex);
            });
        }
      });
    });
  }

  // Function to load the patient orders lookup
  getPatientOrders(patientId: number): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      log.debug('RM Patient Service - fetchPatientOrders invoked', patientId);

      this.baseService.getServiceDetails().subscribe(details => {
        const request: ProxyRequest = { ...details?.lookUp?.fetchPatientOrders };
        if (request && request.configValue && patientId) {
          request.configValue = request.configValue.replace('{id}', patientId.toString());

          this.baseService
            .loadData(appConstants.appURLs.proxyFetch, request)
            .then(data => {
              this.patientData.patientOrders = data as string[];
              resolve(data as string[]);
            })
            .catch(ex => {
              log.error('RecordMgmt fetchPatientOrders : Exception', ex);
              reject(ex);
            });
        }
      });
    });
  }
}
